/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {ExdsButton, ExdsGrid, ExdsLayout, ExdsText, ExdsTooltip} from "@exds/core";
import './CreatePassword.scss';
import {useContext, useEffect, useState} from "react";
import PasswordInputGroup from "../PasswordInputGroup/PasswordInputGroup";
import StartOverModal from "../StartOverModal/StartOverModal";
import {useNavigate} from 'react-router-dom';
import AppContext from "../../AppContext";
import Header from "../Header/Header";
import Error from "../Error/Error";
import {doEncrypt} from "../E2ee/E2ee";
import {apiPost} from "../../service/service";
import Alert from "../Alert/Alert";
import {TuxLoadingIndicator} from "@tuxedo/react-component-library";
import {CANNOT_RESET_PASSWORD, SOMETHING_WENT_WRONG} from "../../constants/constants";
import {addPageLoadEvent} from "../AdobeAnalytics/AdobeAnalytics";
import {isNullOrEmpty, returnLandingPage} from "../../utils/utils";

export default function CreatePassword() {
  const navigate = useNavigate();
  const {sidContext, phoneNumberContext, hashContext, passwordCreatedContext, transactionIdContext, expirationTimeContext, loadAA} = useContext(AppContext);
  const sid = sidContext.at(0);
  const phoneNumber = phoneNumberContext.at(0);
  const setPasswordCreated = passwordCreatedContext.at(1);
  const transactionId = transactionIdContext.at(0);
  const expirationTime = expirationTimeContext.at(0);
  const [hash, setHash] = hashContext;
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isStartOverModalOpen, setIsStartOverModalOpen] = useState(false);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [passphraseTooltip, setPassphraseTooltip] = useState('');
  const [error, setError] = useState({
    hasCredentialPolicyError: false,
    hasSubmitError: false,
    correlationId: null,
    errorMessage: null,
    isLoading: false
  });
  const pageName = 'Create Password';

  useEffect(() => {
    document.title = 'New Hire Portal - Create Password';

    if (isNullOrEmpty(hash)) {
      returnLandingPage();
    }
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if(loadAA) {
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]);

  const submitForm = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    const target = await e.target;
    const formData = target.elements;
    const passwordValue = formData['password-input-field-input'].value;

    setError((prevError) => ({
      ...prevError,
      hasSubmitError: false,
      isLoading: true
    }));

    try {
      const response = await apiPost('/api/resetPassword', {
        sid,
        password: await doEncrypt(passwordValue),
        phoneNumber,
        transactionId,
        expirationTime,
        hash
      });

      if (response.data.success === true && response.data.hash) {
        setHash(response.data.hash);
        setPasswordCreated(true);
        navigate('/securityQuestions');
      } else {
        const errorMessage = response.data.systemErrorMessages && response.data.systemErrorMessages.length > 0 ?
          response.data.systemErrorMessages[0] : CANNOT_RESET_PASSWORD;

        setError((prevError) => ({
          ...prevError,
          hasSubmitError: true,
          correlationId: response.correlationId || 'Unknown',
          errorMessage
        }));
        setFormSubmitted(false);
      }
    } catch (error) {
      setError((prevError) => ({
        ...prevError,
        hasSubmitError: true,
        correlationId: error.response?.data?.correlationId || 'Unknown',
        errorMessage: CANNOT_RESET_PASSWORD
      }));
      setFormSubmitted(false);
    } finally {
      setError((prevError) => ({
        ...prevError,
        isLoading: false
      }));
    }
  };

  return (
    <div className="create-password-container">
      <form onSubmit={submitForm} noValidate>
        <ExdsGrid
          container
          justifyContent="center"
        >
          <Header userValidated currentStep={2} pageName={pageName} sid={sid}/>
          {error.isLoading && <div className="loading-spinner-create-password"><TuxLoadingIndicator/></div>}
          {!error.isLoading && error.hasCredentialPolicyError && <Error
            content={SOMETHING_WENT_WRONG}
            buttonTitle="Start again"
            buttonLink="/validateIdentity"
            correlationId={error.correlationId}
            pageName={pageName}
          />}
          {!error.isLoading && !error.hasCredentialPolicyError &&
            <ExdsLayout
              display="flex"
              alignItems="flex-start"
              flexDirection="column"
              className="password-layout"
            >
              {error.hasSubmitError &&
                <div className="error-create-password">
                  <Alert
                    correlationId={error.correlationId}
                    errorMessage={error.errorMessage}
                    loadAA={loadAA}
                    pageName={pageName}
                    sid={sid}/>
                </div>
              }
              <ExdsText
                primitive="h1"
                textStyle="titleExtraLarge"
                className="mb-075"
              >
                Create password
              </ExdsText>
              <ExdsText primitive="h2" textStyle="bodyMedium" className="sid-label">
                Standard ID (SID):&nbsp;<span className="fw-400">{sid}</span>
              </ExdsText>
              <ExdsText primitive="h3" textStyle="bodyMedium">
                Now you need to create a new password. This will be the password you use regularly to log into all JPMC systems.
              </ExdsText>
              <ExdsText primitive="h3" textStyle="bodyMedium">
                Your password needs to be at least 16 characters long and may contain letters, numbers, spaces and special characters.
              </ExdsText>
              <ExdsText primitive="h3" textStyle="bodyMedium">
                We recommend using a&nbsp;
                <ExdsTooltip
                  position="right"
                  className="tooltip-styling"
                  text={passphraseTooltip}
                  maxWidth="300px"
                >
                  <ExdsText
                    decoration={[
                      'underline'
                    ]}
                    style={{
                      cursor: 'pointer'
                    }}
                    color="brand"
                    textStyle="bodyLargeHeavier"
                  >
                    passphrase.
                  </ExdsText>
                </ExdsTooltip>
              </ExdsText>
              <fieldset className="password-group-container">
                <PasswordInputGroup
                  formSubmitted={formSubmitted}
                  passwordTypeLabel="Password"
                  type="sid"
                  setIsButtonDisabled={setIsSubmitButtonDisabled}
                  showRequirements
                  showStepper
                  showCharCount
                  afterSubmitChecks={['No commonly used passwords', 'No previous passwords']}
                  setPassphraseTooltip={setPassphraseTooltip}
                  setError={setError}
                />
              </fieldset>
              <div className="buttons-container">
                <ExdsButton
                  type="button"
                  text="Start over"
                  variant="secondary"
                  className="action-button"
                  onClick={() => setIsStartOverModalOpen(true)}
                />
                <ExdsButton
                  type="submit"
                  text="Submit"
                  className="action-button"
                  disabled={isSubmitButtonDisabled}
                />
              </div>
            </ExdsLayout>
          }

          <StartOverModal isStartOverModalOpen={isStartOverModalOpen} setIsStartOverModalOpen={setIsStartOverModalOpen}/>
        </ExdsGrid>
      </form>
    </div>
  );
}
